import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Users, ArrowRightLeft, DollarSign, Clock, ArrowLeft, ArrowRight, Calendar, BarChart3, Banknote, RefreshCw, Building2, Shield } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import api, { formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect, useCallback } from "react";
import MFASetup from "@/components/MFASetup";

const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 text-xs shadow-xl">
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">{p.name}: {formatCurrency(p.value)}</p>
      ))}
    </div>
  );
};

export default function AdminOverview({ user, stats, monthStr, selectedYear, selectedMonth, prevMonth, nextMonth }) {
  const [chartView, setChartView] = useState("day");
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  const fetchChartData = useCallback(async () => {
    try {
      const [dailyRes, monthlyRes] = await Promise.all([
        api.get(`/admin/daily-stats?month=${monthStr}`),
        api.get("/admin/monthly-stats?months=12"),
      ]);
      setDailyData(dailyRes.data.data || []);
      setMonthlyData(monthlyRes.data.data || []);
    } catch { toast.error("Erro ao carregar gráficos"); }
  }, [monthStr]);

  useEffect(() => { fetchChartData(); }, [fetchChartData]);

  const statCards = [
    { label: "Empresas Pagadoras", value: stats?.total_companies || 0, icon: Building2, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Colaboradores", value: stats?.total_employees || 0, icon: Users, color: "text-purple-400", bg: "bg-purple-500/10" },
    { label: "Folha Mensal", value: formatCurrency(stats?.folha_mensal), icon: Banknote, color: "text-cyan-400", bg: "bg-cyan-500/10", isText: true },
    { label: "Antecipações", value: stats?.total_anticipations || 0, icon: ArrowRightLeft, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Pendentes", value: stats?.pending_approvals || 0, icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10" },
    { label: "Volume Bruto", value: formatCurrency(stats?.total_bruto), icon: DollarSign, color: "text-foreground", bg: "bg-white/5", isText: true },
    { label: "Volume Líquido", value: formatCurrency(stats?.total_liquido), icon: DollarSign, color: "text-emerald-400", bg: "bg-emerald-500/10", isText: true },
    { label: "Total Deságio", value: formatCurrency((stats?.total_bruto || 0) - (stats?.total_liquido || 0)), icon: DollarSign, color: "text-yellow-400", bg: "bg-yellow-500/10", isText: true },
  ];

  const chartDataDay = dailyData.map((d) => ({ ...d, date: d.date.slice(5) }));
  const chartDataMonth = monthlyData.filter(d => d.volume > 0 || d.count > 0);
  const activeChartData = chartView === "day" ? chartDataDay : chartDataMonth;
  const xKey = chartView === "day" ? "date" : "label";

  return (
    <>
      {user && !user.mfa_enabled && (
        <div className={`p-4 rounded-xl border ${user.mfa_blocked ? "bg-red-500/15 border-red-500/30" : "bg-red-500/10 border-red-500/20"}`}>
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-400">{user.mfa_blocked ? "Acesso bloqueado — 2FA obrigatório" : "Autenticação de dois fatores obrigatória"}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {user.mfa_blocked ? "Configure o 2FA abaixo para desbloquear." : user.mfa_grace_days_left != null ? `${user.mfa_grace_days_left} dias restantes para ativar.` : "Configure abaixo."}
              </p>
            </div>
          </div>
        </div>
      )}
      {user?.mfa_blocked ? (
        <MFASetup />
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 stagger-children" data-testid="admin-overview-stats">
        {statCards.map((c) => (
          <Card key={c.label} className="border-white/5 animate-fade-in card-interactive">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
                <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}>
                  <c.icon className={`w-4 h-4 ${c.color}`} />
                </div>
              </div>
              <p className={`font-bold tracking-tight ${c.isText ? "text-xl" : "text-2xl"}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <KredzBalanceCard />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" /> Gráficos</h2>
          <div className="flex bg-card border border-border rounded-lg overflow-hidden">
            <button onClick={() => setChartView("day")} data-testid="chart-view-day"
              className={`px-3 py-1 text-xs font-semibold transition-colors ${chartView === "day" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>Dia</button>
            <button onClick={() => setChartView("month")} data-testid="chart-view-month"
              className={`px-3 py-1 text-xs font-semibold transition-colors ${chartView === "month" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>Mês</button>
          </div>
        </div>
        <Card className="border-white/5">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Volume Antecipado {chartView === "day" ? "por Dia" : "por Mês"}</CardTitle></CardHeader>
          <CardContent>
            {activeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={activeChartData}>
                  <defs>
                    <linearGradient id="volumeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="volume" name="Volume Bruto" stroke="#3B82F6" fill="url(#volumeGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="liquido" name="Volume Líquido" stroke="#10B981" fill="transparent" strokeWidth={2} strokeDasharray="4 4" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">Sem dados para o período</div>}
          </CardContent>
        </Card>
        <Card className="border-white/5">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Deságio Faturado {chartView === "day" ? "por Dia" : "por Mês"}</CardTitle></CardHeader>
          <CardContent>
            {activeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={activeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${v.toFixed(0)}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="desagio" name="Deságio" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">Sem dados para o período</div>}
          </CardContent>
        </Card>
      </div>

      {stats?.status_breakdown && Object.keys(stats.status_breakdown).length > 0 && (
        <Card className="border-white/5" data-testid="admin-status-breakdown">
          <CardHeader><CardTitle className="text-base">Status das Antecipações</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.status_breakdown).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2"><StatusBadge status={status} /> <span className="text-sm font-semibold">{count}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <MFASetup />
      <SyncPanel />
      </>
      )}
    </>
  );
}

function SyncPanel() {
  const [syncing, setSyncing] = useState(null);
  const [results, setResults] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  const runSync = async (type) => {
    setSyncing(type);
    setResults(null);
    setErrorDetail(null);
    try {
      const res = await api.post(`/admin/sync-${type}`);
      setResults({ type, data: res.data });
      const totalErrors = Object.values(res.data).reduce((s, v) => s + (v.errors?.length || 0), 0);
      const interrompido = Object.values(res.data).find((v) => v.interrompido)?.interrompido;
      if (interrompido) {
        toast.warning(
          interrompido === "rate_limit"
            ? "Reprocessamento parcial: limite de requisições da Zemo; tente de novo em alguns minutos"
            : `Reprocessamento parcial — interrompido: ${interrompido}`
        );
      } else if (totalErrors > 0) {
        toast.warning(`Sincronização concluída com ${totalErrors} erro(s)`);
      } else {
        toast.success("Sincronização Zemo concluída");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro na sincronização");
    }
    setSyncing(null);
  };

  return (
    <Card className="border-white/5" data-testid="sync-panel">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4 text-blue-400" /> Sincronização com APIs Externas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="p-4 rounded-lg bg-black/10 border border-white/5 space-y-3">
            <div>
              <p className="text-sm font-medium">Zemo (Antecipações)</p>
              <p className="text-xs text-muted-foreground">Cadastra empresas como pagadoras na Zemo</p>
            </div>
            <Button size="sm" onClick={() => runSync("zemo-v2-payers")} disabled={!!syncing}
              data-testid="sync-zemo-v2-payers-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs w-full">
              {syncing === "zemo-v2-payers" ? <div className="loading-spinner" /> : "Reprocessar pagadores na Zemo"}
            </Button>
            <p className="text-[10px] text-muted-foreground">Cadastra todas as empresas aprovadas como pagadoras na Zemo para operações de antecipação. Pula as já cadastradas.</p>
          </div>
        </div>
        {results && (
          <div className="p-3 rounded-lg bg-black/20 border border-white/5 text-xs space-y-2 animate-fade-in" data-testid="sync-results">
            <p className="font-semibold text-emerald-400">Resultado — Zemo</p>
            {Object.entries(results.data).map(([key, val]) => (
              <div key={key} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground capitalize">{key}: </span>
                  <span className="text-emerald-400">{val.synced} sincronizados</span>
                  {val.skipped > 0 && <span className="text-muted-foreground"> · {val.skipped} já existentes</span>}
                  {val.interrompido && <span className="text-yellow-400"> · parcial — interrompido: {val.interrompido}</span>}
                  {val.errors?.length > 0 && (
                    <button onClick={() => setErrorDetail({ category: key, errors: val.errors })}
                      className="text-red-400 hover:text-red-300 underline cursor-pointer">
                      {val.errors.length} erro(s) — ver detalhes
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!errorDetail} onOpenChange={() => setErrorDetail(null)}>
        <DialogContent className="sm:max-w-lg border-white/10 max-h-[70vh] overflow-y-auto" data-testid="sync-error-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400">Erros de Sincronização — {errorDetail?.category}</DialogTitle>
            <DialogDescription>{errorDetail?.errors?.length} item(ns) falharam</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {errorDetail?.errors?.map((e, i) => (
              <div key={i} className="p-3 rounded-lg bg-red-500/5 border border-red-500/10 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{e.name || e.id}</span>
                  {e.id && <span className="text-muted-foreground font-mono text-[10px]">{e.id.slice(0, 8)}...</span>}
                </div>
                <p className="text-red-400">{e.codigo}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setErrorDetail(null)} className="border-white/10">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function KredzBalanceCard() {
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showStatement, setShowStatement] = useState(false);

  useEffect(() => {
    api.get("/ledger/admin/balance").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const loadStatement = async () => {
    try {
      const r = await api.get("/ledger/admin/statement?page_size=50");
      setEntries(r.data.items || []);
      setShowStatement(true);
    } catch { toast.error("Erro ao carregar extrato"); }
  };

  if (!data) return null;

  return (
    <Card className="border-white/5 animate-fade-in" data-testid="kredz-balance-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Receita Kredz (Taxas)</p>
              <p className="text-2xl font-bold text-emerald-400" data-testid="kredz-balance-value">{formatCurrency(data.balance)}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadStatement}
            data-testid="view-kredz-statement-btn"
            className="h-8 text-xs border-white/10">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Extrato
          </Button>
        </div>
      </CardContent>

      <Dialog open={showStatement} onOpenChange={setShowStatement}>
        <DialogContent className="sm:max-w-lg border-white/10 max-h-[80vh] overflow-y-auto" data-testid="kredz-statement-dialog">
          <DialogHeader>
            <DialogTitle>Extrato Kredz — Taxas Coletadas</DialogTitle>
            <DialogDescription>Saldo acumulado: {formatCurrency(data.balance)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhuma entrada registrada</p>
            ) : entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-2.5 rounded-lg bg-black/10 border border-white/5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{e.description || e.category}</p>
                  <p className="text-[10px] text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</p>
                </div>
                <span className="text-emerald-400 font-semibold text-sm ml-3">+{formatCurrency(e.amount)}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatement(false)} className="border-white/10">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
